import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Building2, Factory, MapPin, Store, ArrowRight, CheckCircle, Landmark } from "lucide-react";

const Properties = () => {
  const assetTypes = [
    {
      icon: Building2,
      title: "Office",
      description: "Professional office spaces for businesses of all sizes",
      features: [
        "Executive suites and corporate headquarters",
        "Medical and professional office buildings",
        "Flexible co-working and shared office spaces",
        "Class A, B, and C office properties",
        "Build-to-suit office developments"
      ],
      image: "https://images.unsplash.com/photo-1760124056893-6b2e8a11745a?w=600&auto=format&fit=crop&q=80",
      benefits: [
        "Prime El Paso locations",
        "Flexible lease terms",
        "Professional environments",
        "Scalable spaces"
      ]
    },
    {
      icon: Store,
      title: "Retail",
      description: "High-visibility retail locations for maximum customer exposure",
      features: [
        "Strip centers and shopping plazas",
        "Standalone retail buildings",
        "Restaurant and food service locations",
        "Anchor tenant opportunities",
        "Ground floor retail in mixed-use developments"
      ],
      image: "https://images.unsplash.com/photo-1758448500717-2e4bcd79108b?w=600&auto=format&fit=crop&q=80",
      benefits: [
        "High foot traffic areas",
        "Excellent visibility",
        "Ample parking",
        "Growing market presence"
      ]
    },
    {
      icon: Factory,
      title: "Industrial",
      description: "Warehouse, manufacturing, and distribution facilities",
      features: [
        "Warehouse and distribution centers",
        "Manufacturing facilities",
        "Flex space and light industrial",
        "Cold storage and specialized facilities",
        "Logistics and transportation hubs"
      ],
      image: "https://images.unsplash.com/photo-1669003750652-2027bd69e7a1?w=600&auto=format&fit=crop&q=80",
      benefits: [
        "Strategic locations",
        "Highway access",
        "Loading dock facilities",
        "Scalable operations"
      ]
    },
    {
      icon: Landmark,
      title: "Land",
      description: "Development opportunities and investment land parcels",
      features: [
        "Commercial development sites",
        "Industrial land parcels",
        "Mixed-use development opportunities",
        "Investment and hold properties",
        "Build-to-suit land packages"
      ],
      image: "https://images.unsplash.com/photo-1761002278107-dc5d4f800834?w=600&auto=format&fit=crop&q=80",
      benefits: [
        "Prime development sites",
        "Zoning flexibility",
        "Growth potential",
        "Investment opportunities"
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-background">
      {/* Navigation */}
      <nav className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center">
              <img 
                src="./images/KWCommercial_Stacked_RGB.png" 
                alt="KW Commercial" 
                className="h-12 w-auto"
              />
            </div>
            <div className="hidden md:flex space-x-8">
              <a href="/" className="text-foreground hover:text-primary transition-smooth">Home</a>
              <a href="/services" className="text-foreground hover:text-primary transition-smooth">Services</a>
              <a href="/properties" className="text-primary font-semibold">Asset Types</a>
              <a href="/about" className="text-foreground hover:text-primary transition-smooth">About</a>
              <a href="/contact" className="text-foreground hover:text-primary transition-smooth">Contact</a>
            </div>
            <Button className="btn-primary">
              Get Started
            </Button>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="py-20 bg-secondary/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-5xl font-bold mb-6">Commercial Asset Types</h1>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
            Angel Cazares specializes in four key commercial real estate asset types in El Paso: 
            Industrial, Office, Land, and Retail properties.
          </p>
        </div>
      </section>

      {/* Asset Types */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-20">
            {assetTypes.map((asset, index) => {
              const Icon = asset.icon;
              const isEven = index % 2 === 0;
              
              return (
                <div key={index} className={`grid lg:grid-cols-2 gap-12 items-center ${!isEven ? 'lg:grid-flow-col-dense' : ''}`}>
                  <div className={isEven ? 'lg:order-1' : 'lg:order-2'}>
                    <div className="flex items-center mb-6">
                      <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mr-4">
                        <Icon className="h-6 w-6 text-primary" />
                      </div>
                      <h2 className="text-3xl font-bold">{asset.title}</h2>
                    </div>
                    <p className="text-lg text-muted-foreground mb-6">
                      {asset.description}
                    </p>
                    
                    <div className="mb-6">
                      <h3 className="text-lg font-semibold mb-3">Property Types:</h3>
                      <ul className="space-y-2">
                        {asset.features.map((feature, featureIndex) => (
                          <li key={featureIndex} className="flex items-center">
                            <div className="w-2 h-2 bg-primary rounded-full mr-3"></div>
                            <span>{feature}</span>
                          </li>
                        ))}
                      </ul>
                    </div>

                    <div className="mb-8">
                      <h3 className="text-lg font-semibold mb-3">Key Benefits:</h3>
                      <div className="grid grid-cols-2 gap-2">
                        {asset.benefits.map((benefit, benefitIndex) => (
                          <div key={benefitIndex} className="flex items-center">
                            <CheckCircle className="h-4 w-4 text-primary mr-2" />
                            <span className="text-sm">{benefit}</span>
                          </div>
                        ))}
                      </div>
                    </div>

                    <Button className="btn-primary">
                      Learn More <ArrowRight className="ml-2 h-4 w-4" />
                    </Button>
                  </div>
                  <div className={isEven ? 'lg:order-2' : 'lg:order-1'}>
                    <img 
                      src={asset.image} 
                      alt={asset.title}
                      className="rounded-lg shadow-elegant w-full h-[400px] object-cover"
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* El Paso Market Focus */}
      <section className="py-20 bg-secondary/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold mb-4">El Paso Commercial Real Estate</h2>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
              Angel Cazares brings deep knowledge of El Paso's commercial landscape to every client relationship, 
              serving diverse clients from local entrepreneurs to established business owners and investors.
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            <Card className="shadow-card text-center">
              <CardHeader>
                <CardTitle className="text-2xl text-primary">Local</CardTitle>
                <CardDescription>Entrepreneurs</CardDescription>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground">
                  First-time business owners seeking their ideal commercial space
                </p>
              </CardContent>
            </Card>
            
            <Card className="shadow-card text-center">
              <CardHeader>
                <CardTitle className="text-2xl text-primary">Medical</CardTitle>
                <CardDescription>Professionals</CardDescription>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground">
                  Specialized office spaces for healthcare and medical practices
                </p>
              </CardContent>
            </Card>
            
            <Card className="shadow-card text-center">
              <CardHeader>
                <CardTitle className="text-2xl text-primary">Restaurant</CardTitle>
                <CardDescription>Operators</CardDescription>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground">
                  High-visibility locations for food service businesses
                </p>
              </CardContent>
            </Card>
            
            <Card className="shadow-card text-center">
              <CardHeader>
                <CardTitle className="text-2xl text-primary">Property</CardTitle>
                <CardDescription>Investors</CardDescription>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground">
                  Income-producing properties with strong ROI potential
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold mb-6">Ready to Find Your Perfect Property?</h2>
          <p className="text-xl text-muted-foreground mb-8">
            Whether you're looking for office space, retail locations, industrial facilities, or development land, 
            Angel Cazares can help you find the right opportunity in El Paso.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button size="lg" className="btn-primary px-8">
              Schedule Consultation
            </Button>
            <Button size="lg" variant="outline" className="px-8">
              Contact Angel Today
            </Button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-foreground text-background py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-4 gap-8">
            <div>
              <img 
                src="./images/KWCommercial_Stacked_RGB.png" 
                alt="KW Commercial" 
                className="h-12 w-auto mb-4"
              />
              <p className="text-background/80">
                Your trusted partner in El Paso commercial real estate.
              </p>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Services</h4>
              <ul className="space-y-2 text-background/80">
                <li>Tenant Representation</li>
                <li>Investment Sales</li>
                <li>Commercial Leasing</li>
                <li>Market Analysis</li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Asset Types</h4>
              <ul className="space-y-2 text-background/80">
                <li>Office Buildings</li>
                <li>Retail Centers</li>
                <li>Industrial Properties</li>
                <li>Development Land</li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Contact Angel</h4>
              <ul className="space-y-2 text-background/80">
                <li>El Paso, Texas</li>
                <li>KW Commercial</li>
                <li>Bilingual (English & Spanish)</li>
              </ul>
            </div>
          </div>
          <div className="border-t border-background/20 mt-8 pt-8 text-center text-background/60">
            <p>&copy; 2024 Angel Cazares - KW Commercial. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Properties;